import React from "react";
import { FaAndroid, FaApple, FaInstagram } from "react-icons/fa";
import { MdWhatshot } from "react-icons/md";
import { RiRadarLine } from "react-icons/ri";
import {
  FiActivity,
  FiMap,
  FiMessageCircle,
  FiNavigation,
  FiTrendingUp,
} from "react-icons/fi";

import bannerApp from "../../assets/banner-app.png";
import brand from "../../assets/brand.png";

import {
  benefitPillars,
  featureHighlights,
  heroStats,
  productLoopSteps,
} from "./content";
import {
  BrandCopy,
  BrandLink,
  BrandMark,
  ButtonCopy,
  ButtonHint,
  ButtonIcon,
  ButtonStack,
  ButtonTitle,
  ClosingCard,
  ClosingText,
  ClosingTitle,
  DemoGrid,
  DemoSurface,
  FeatureCard,
  FeatureDescription,
  FeatureEyebrow,
  FeatureGrid,
  FeatureIcon,
  FeatureImpact,
  FeatureTitle,
  FeedPreviewImage,
  FeedShell,
  Footer,
  FooterAnchor,
  FooterLinks,
  FooterNote,
  Glow,
  HeroStatBadge,
  HeroActions,
  HeroContent,
  HeroDescription,
  HeroEyebrow,
  HeroMedia,
  HeroSection,
  HeroStats,
  HeroTitle,
  IntroBanner,
  IntroBannerCaption,
  IntroBannerImage,
  LegendItem,
  LoopCard,
  LoopGrid,
  LoopStep,
  LoopText,
  LoopTitle,
  MapCanvas,
  MapLegend,
  MapPreviewImage,
  MapShell,
  MiniFeatureBadge,
  MiniFeatureCard,
  MiniFeatureText,
  MiniFeatureTitle,
  NavLink,
  NavLinks,
  Page,
  PillarCard,
  PillarGrid,
  PillarText,
  PillarTitle,
  PrimaryButton,
  SecondaryButton,
  Section,
  SectionDescription,
  SectionEyebrow,
  SectionHead,
  SectionTitle,
  Shell,
  ShellEyebrow,
  ShellHeader,
  ShellText,
  ShellTitle,
  StatCard,
  StatLabel,
  StatTitleRow,
  StatValue,
  StatValueInline,
  TopAction,
  TopBar,
} from "./styles";

const APP_STORE_URL = "https://apps.apple.com/br/app/ondehj/id6764485556";

const featureIcons: Record<string, React.ReactNode> = {
  livemap: <FiActivity />,
  chat: <FiMessageCircle />,
  radius: <FiMap />,
};

const heroStatAccents: Record<
  string,
  { icon: React.ReactNode; variant: "fire" | "message" | "radar" }
> = {
  LiveMap: { icon: <MdWhatshot size={16} />, variant: "fire" },
  "Grupo do evento": { icon: <FiMessageCircle size={15} />, variant: "message" },
  "Até 70 km": { icon: <RiRadarLine size={16} />, variant: "radar" },
};

const LandingPage: React.FC = () => {
  return (
    <Page>
      <Glow />
      <Shell>
        <TopBar>
          <BrandLink href="/">
            <BrandMark alt="OndeHJ" src={brand} />
            <BrandCopy>
              Descubra eventos, lugares e o que está bombando perto de você.
            </BrandCopy>
          </BrandLink>

          <NavLinks>
            <NavLink href="#diferenciais">Diferenciais</NavLink>
            <NavLink href="#como-funciona">Como funciona</NavLink>
            <NavLink href="#motivos">Por que baixar</NavLink>
            <NavLink href="/livemap">LiveMap</NavLink>
            <NavLink href="/contact">Contato</NavLink>
          </NavLinks>

          <TopAction href={APP_STORE_URL} rel="noreferrer" target="_blank">
            <ButtonStack>
              <ButtonIcon>
                <FaApple />
              </ButtonIcon>
              <ButtonTitle>Baixar na App Store</ButtonTitle>
            </ButtonStack>
          </TopAction>
        </TopBar>

        <IntroBannerCaption>
          Encontre o rolê que você procura, converse com as pessoas no grupo do
          evento dentro do app e compartilhe tudo nas redes sociais.
        </IntroBannerCaption>

        <IntroBanner>
          <IntroBannerImage alt="Banner de apresentação do app OndeHJ" src={bannerApp} />
        </IntroBanner>

        <HeroSection>
          <HeroContent>
            <HeroEyebrow>Descoberta urbana em tempo real</HeroEyebrow>

            <HeroTitle>
              Mapa de calor em tempo real, conversa em grupo e compartilhamento nas
              redes sociais.
            </HeroTitle>

            <HeroDescription>
              O OndeHJ junta mapa de calor, visualização em lista e mapa, grupos por
              evento e compartilhamento nos Stories para ajudar você a decidir o rolê
              mais rápido e entrar no clima antes mesmo de sair de casa.
            </HeroDescription>

            <HeroActions>
              <PrimaryButton href={APP_STORE_URL} rel="noreferrer" target="_blank">
                <ButtonStack>
                  <ButtonIcon>
                    <FaApple />
                  </ButtonIcon>
                  <ButtonCopy>
                    <ButtonHint>Disponível agora</ButtonHint>
                    <ButtonTitle>Baixar na App Store</ButtonTitle>
                  </ButtonCopy>
                </ButtonStack>
              </PrimaryButton>

              <SecondaryButton as="span" aria-disabled="true">
                <ButtonStack>
                  <ButtonIcon>
                    <FaAndroid />
                  </ButtonIcon>
                  <ButtonCopy>
                    <ButtonHint>Android</ButtonHint>
                    <ButtonTitle>Play Store em breve</ButtonTitle>
                  </ButtonCopy>
                </ButtonStack>
              </SecondaryButton>
            </HeroActions>

            <HeroStats>
              {heroStats.map((stat) =>
                stat.value === "Instagram Stories" ? (
                  <MiniFeatureCard key={stat.value}>
                    <MiniFeatureBadge>
                      <FaInstagram />
                      Instagram Stories
                    </MiniFeatureBadge>
                    <MiniFeatureTitle>Compartilhe nos Stories em segundos.</MiniFeatureTitle>
                    <MiniFeatureText>
                      O evento sai do app com narrativa visual e alcance social imediato.
                    </MiniFeatureText>
                  </MiniFeatureCard>
                ) : (
                  <StatCard key={stat.value}>
                    {heroStatAccents[stat.value] ? (
                      <StatTitleRow>
                        <StatValueInline>{stat.value}</StatValueInline>
                        <HeroStatBadge
                          $variant={heroStatAccents[stat.value].variant}
                          aria-hidden="true"
                        >
                          {heroStatAccents[stat.value].icon}
                        </HeroStatBadge>
                      </StatTitleRow>
                    ) : (
                      <StatValue>{stat.value}</StatValue>
                    )}
                    <StatLabel>{stat.label}</StatLabel>
                  </StatCard>
                ),
              )}
            </HeroStats>
          </HeroContent>

          <HeroMedia>
            <DemoSurface>
              <DemoGrid>
                <FeedShell>
                  <ShellHeader>
                    <ShellEyebrow>Lista + filtros</ShellEyebrow>
                    <ShellTitle>Descoberta rápida com contexto visual.</ShellTitle>
                    <ShellText>
                      O usuário alterna entre lista e mapa e filtra por raio, vibe e
                      momento.
                    </ShellText>
                  </ShellHeader>

                  <FeedPreviewImage
                    alt="Visualização do app em lista e mapa com filtros"
                    src="/prints/lista-filtros-preview.jpg"
                  />
                </FeedShell>

                <MapShell>
                  <ShellHeader>
                    <ShellEyebrow>LiveMap + social</ShellEyebrow>
                    <ShellTitle>Mapa de calor, grupos e Stories no mesmo lugar.</ShellTitle>
                    <ShellText>
                      A descoberta geográfica ganha leitura visual e continua no grupo do
                      evento e no compartilhamento.
                    </ShellText>
                  </ShellHeader>

                  <MapCanvas>
                    <MapPreviewImage
                      alt="Mapa com pontos coloridos destacando regiões em alta"
                      src="/prints/livemap-social-preview.png"
                    />
                  </MapCanvas>

                  <MapLegend>
                    <LegendItem>
                      <FiActivity />
                      Mapa de calor
                    </LegendItem>
                    <LegendItem>
                      <FiNavigation />
                      Lista e mapa
                    </LegendItem>
                    <LegendItem>
                      <FiTrendingUp />
                      Atividade local
                    </LegendItem>
                  </MapLegend>
                </MapShell>
              </DemoGrid>
            </DemoSurface>
          </HeroMedia>
        </HeroSection>

        <Section id="diferenciais">
          <SectionHead>
            <SectionEyebrow>Diferenciais do app</SectionEyebrow>
            <SectionTitle>
              Os quatro pontos que fazem o OndeHJ bater o olho e parecer diferente.
            </SectionTitle>
            <SectionDescription>
              Em poucos segundos você entende o que torna o app mais visual, social e
              rápido para descobrir o próximo rolê.
            </SectionDescription>
          </SectionHead>

          <FeatureGrid>
            {featureHighlights.map((feature) => (
              <FeatureCard key={feature.id}>
                <FeatureIcon>{featureIcons[feature.id]}</FeatureIcon>
                <FeatureEyebrow>{feature.eyebrow}</FeatureEyebrow>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
                <FeatureImpact>{feature.impact}</FeatureImpact>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Section>

        <Section id="como-funciona">
          <SectionHead>
            <SectionEyebrow>Como funciona</SectionEyebrow>
            <SectionTitle>
              Descoberta, conversa e compartilhamento acontecem no mesmo fluxo.
            </SectionTitle>
            <SectionDescription>
              O evento não termina quando você encontra o rolê. Ele pode virar grupo,
              combinação entre amigos e compartilhamento no Instagram.
            </SectionDescription>
          </SectionHead>

          <LoopGrid>
            {productLoopSteps.map((step) => (
              <LoopCard key={step.id}>
                <LoopStep>{step.step}</LoopStep>
                <LoopTitle>{step.title}</LoopTitle>
                <LoopText>{step.description}</LoopText>
              </LoopCard>
            ))}
          </LoopGrid>
        </Section>

        <Section id="motivos">
          <SectionHead>
            <SectionEyebrow>Por que baixar</SectionEyebrow>
            <SectionTitle>
              Não é só uma agenda de eventos. É um jeito mais vivo de decidir para onde ir.
            </SectionTitle>
            <SectionDescription>
              O OndeHJ junta descoberta local, energia do momento, conversa em grupo e
              compartilhamento visual em uma experiência pensada para quem decide sair na
              hora.
            </SectionDescription>
          </SectionHead>

          <PillarGrid>
            {benefitPillars.map((pillar) => (
              <PillarCard key={pillar.id}>
                <PillarTitle>{pillar.title}</PillarTitle>
                <PillarText>{pillar.description}</PillarText>
              </PillarCard>
            ))}
          </PillarGrid>
        </Section>

        <ClosingCard>
          <ClosingTitle>
            Baixe o OndeHJ e veja o que está bombando perto de você em lista, mapa e no
            LiveMap.
          </ClosingTitle>
          <ClosingText>
            Descubra eventos no raio que faz sentido para você, entre no grupo do evento
            e compartilhe o rolê nos Stories sem sair da experiência.
          </ClosingText>

          <HeroActions>
            <PrimaryButton href={APP_STORE_URL} rel="noreferrer" target="_blank">
              <ButtonStack>
                <ButtonIcon>
                  <FaApple />
                </ButtonIcon>
                <ButtonCopy>
                  <ButtonHint>Disponível agora</ButtonHint>
                  <ButtonTitle>Baixar na App Store</ButtonTitle>
                </ButtonCopy>
              </ButtonStack>
            </PrimaryButton>

            <SecondaryButton as="span" aria-disabled="true">
              <ButtonStack>
                <ButtonIcon>
                  <FaAndroid />
                </ButtonIcon>
                <ButtonCopy>
                  <ButtonHint>Android</ButtonHint>
                  <ButtonTitle>Play Store em breve</ButtonTitle>
                </ButtonCopy>
              </ButtonStack>
            </SecondaryButton>
          </HeroActions>
        </ClosingCard>

        <Footer>
          <FooterNote>
            OndeHJ. Descoberta local em tempo real com LiveMap, grupos por evento,
            visualização em lista e mapa e compartilhamento nos Stories.
          </FooterNote>

          <FooterLinks>
            <FooterAnchor href="/livemap">LiveMap</FooterAnchor>
            <FooterAnchor href="/about">Sobre</FooterAnchor>
            <FooterAnchor href="/contact">Contato</FooterAnchor>
            <FooterAnchor href="/privacy">Privacidade</FooterAnchor>
            <FooterAnchor href="/termos-de-uso">Termos</FooterAnchor>
          </FooterLinks>
        </Footer>
      </Shell>
    </Page>
  );
};

export default LandingPage;
